import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

// Define dimension presets for common use cases
interface DimensionPreset {
  name: string;
  width: number;
  height: number;
  description: string;
}

// Define style presets
interface StylePreset {
  name: string;
  description: string;
  promptPrefix: string;
}

// Log entry interface for debug panel
interface LogEntry {
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'success';
  message: string;
  data?: any;
}

const TextToImageGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [enhancementLoading, setEnhancementLoading] = useState(false);
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const [previewEnhancedPrompt, setPreviewEnhancedPrompt] = useState<string | null>(null);
  const [editableEnhancedPrompt, setEditableEnhancedPrompt] = useState<string>('');
  const [isEditingEnhancedPrompt, setIsEditingEnhancedPrompt] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [enhancePrompt, setEnhancePrompt] = useState(true);
  const [enhancedPrompt, setEnhancedPrompt] = useState<string | null>(null);
  const [originalPrompt, setOriginalPrompt] = useState<string | null>(null);
  const [width, setWidth] = useState<number>(1024);
  const [height, setHeight] = useState<number>(1024);
  const [selectedPreset, setSelectedPreset] = useState<string>('square');
  const [selectedStyle, setSelectedStyle] = useState<string>('');
  const [developerMode, setDeveloperMode] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Dimension presets - all dimensions must be multiples of 64 between 128 and 2048
  const dimensionPresets: DimensionPreset[] = [
    { name: 'square', width: 1024, height: 1024, description: 'Perfect for social media posts' },
    { name: 'portrait', width: 768, height: 1024, description: 'Ideal for stories and mobile' },
    { name: 'landscape', width: 1024, height: 768, description: 'Great for website headers' },
    { name: 'banner', width: 1024, height: 512, description: 'YouTube and website banners' },
    { name: 'instagram', width: 1024, height: 1024, description: 'Instagram optimized' },
    { name: 'thumbnail', width: 512, height: 512, description: 'Small preview images' }
  ];

  // Validate dimensions to ensure they comply with API requirements
  const validateDimensions = (dimension: number): number => {
    // Ensure dimension is between 128 and 2048
    let validDimension = Math.max(128, Math.min(2048, dimension));
    // Ensure dimension is a multiple of 64
    validDimension = Math.round(validDimension / 64) * 64;
    return validDimension;
  };

  // Style presets
  const stylePresets: StylePreset[] = [
    { name: 'Photorealistic', description: 'Realistic photography style', promptPrefix: 'Photorealistic image of' },
    { name: 'Digital Art', description: 'Modern digital illustration', promptPrefix: 'Digital art of' },
    { name: 'Watercolor', description: 'Soft watercolor painting', promptPrefix: 'Watercolor painting of' },
    { name: 'Abstract', description: 'Abstract artistic interpretation', promptPrefix: 'Abstract art representing' },
    { name: 'Cinematic', description: 'Movie-like scene with dramatic lighting', promptPrefix: 'Cinematic scene of' },
    { name: 'Minimalist', description: 'Clean, simple design with few elements', promptPrefix: 'Minimalist design of' }
  ];

  // Example prompts for inspiration
  const examplePrompts = [
    'A serene mountain landscape at sunset with a lake reflecting the colorful sky',
    'A modern, minimalist logo for a tech company with blue and green colors',
    'A cozy coffee shop interior with warm lighting, wooden furniture, and plants',
    'A futuristic city skyline at night with neon lights and flying vehicles',
    'An overhead view of a beautifully plated gourmet meal with vibrant colors'
  ];

  // Handle selecting a dimension preset
  const handlePresetChange = (presetName: string) => {
    const preset = dimensionPresets.find(p => p.name === presetName);
    if (preset) {
      setWidth(validateDimensions(preset.width));
      setHeight(validateDimensions(preset.height));
      setSelectedPreset(presetName);
    }
  };

  // Handle selecting a style preset
  const handleStyleChange = (styleName: string) => {
    setSelectedStyle(styleName);
  };

  // Handle using an example prompt
  const handleUseExample = (examplePrompt: string) => {
    setPrompt(examplePrompt);
  };

  // Handle applying a style to the current prompt
  const applyStyle = () => {
    if (!selectedStyle) return;
    
    const style = stylePresets.find(s => s.name === selectedStyle);
    if (!style) return;
    
    // Check if the prompt already starts with the style prefix
    if (!prompt.startsWith(style.promptPrefix)) {
      setPrompt(style.promptPrefix + " " + prompt);
    }
  };

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Apply style when it changes
  React.useEffect(() => {
    if (selectedStyle && prompt) {
      applyStyle();
    }
  }, [selectedStyle]);

  // Auto-scroll logs to bottom when new logs are added
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Add log entry
  const addLog = (level: LogEntry['level'], message: string, data?: any) => {
    if (!developerMode) return;
    
    const now = new Date();
    const timestamp = now.toLocaleTimeString() + '.' + now.getMilliseconds().toString().padStart(3, '0');
    
    setLogs(prevLogs => [
      ...prevLogs,
      { timestamp, level, message, data }
    ]);
  };

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
    addLog('info', 'Logs cleared');
  };

  // Copy logs to clipboard
  const copyLogs = () => {
    const logText = logs.map(log => `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message} ${log.data ? JSON.stringify(log.data) : ''}`).join('\n');
    navigator.clipboard.writeText(logText);
    toast.success('Logs copied to clipboard!');
  };

  // Copy prompt to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
    addLog('info', 'Copied text to clipboard', { text });
  };

  // Preview enhanced prompt without generating image
  const previewEnhancement = async () => {
    if (!prompt) {
      toast.error('Please enter a prompt first');
      return;
    }

    setEnhancementLoading(true);
    setEnhancementError(null);
    addLog('info', 'Requesting prompt enhancement preview', { originalPrompt: prompt });

    try {
      const userId = await getToken();
      if (!userId) {
        throw new Error('Authentication not available. Please log in again.');
      }

      const response = await fetch('/api/marketing/enhance-prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId,
          prompt
        })
      });

      const data = await response.json();

      if (!response.ok) {
        addLog('error', 'Prompt enhancement preview failed', { error: data.error, status: response.status });
        throw new Error(data.error || 'Failed to enhance prompt');
      }

      if (!data.enhancedPrompt) {
        throw new Error('No enhanced prompt returned');
      }

      setPreviewEnhancedPrompt(data.enhancedPrompt);
      setEditableEnhancedPrompt(data.enhancedPrompt);
      addLog('success', 'Prompt enhancement preview successful', { enhancedPrompt: data.enhancedPrompt });
      toast.success('Prompt enhanced!');
    } catch (error) {
      addLog('error', 'Error enhancing prompt', { error });
      setEnhancementError(error instanceof Error ? error.message : 'Failed to enhance prompt');
      toast.error(error instanceof Error ? error.message : 'Failed to enhance prompt');
    } finally {
      setEnhancementLoading(false);
    }
  };

  // Handle edit toggling for enhanced prompt
  const toggleEnhancedPromptEdit = () => {
    if (isEditingEnhancedPrompt) {
      // Save changes
      setPreviewEnhancedPrompt(editableEnhancedPrompt);
      addLog('info', 'Enhanced prompt edited', { newPrompt: editableEnhancedPrompt });
    } else {
      // Start editing
      setEditableEnhancedPrompt(previewEnhancedPrompt || '');
      addLog('info', 'Started editing enhanced prompt');
    }
    setIsEditingEnhancedPrompt(!isEditingEnhancedPrompt);
  };

  // Apply enhanced prompt as the base prompt
  const applyEnhancedPrompt = () => {
    if (previewEnhancedPrompt) {
      setPrompt(previewEnhancedPrompt);
      setPreviewEnhancedPrompt(null);
      setEditableEnhancedPrompt('');
      toast.success('Enhanced prompt applied!');
      addLog('info', 'Applied enhanced prompt to base prompt');
    }
  };

  // Toggle developer mode
  const toggleDeveloperMode = () => {
    setDeveloperMode(!developerMode);
    if (!developerMode) {
      addLog('info', 'Developer mode activated');
    }
  };

  const handleGenerateImage = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    addLog('info', 'Starting image generation', { prompt, enhancePrompt, width, height });
    try {
      const userId = await getToken();
      if (!userId) {
        throw new Error('Authentication not available. Please log in again.');
      }
      
      // Ensure dimensions meet API requirements before sending
      const validatedWidth = validateDimensions(width);
      const validatedHeight = validateDimensions(height);
      
      // Update state if validation changed the values
      if (validatedWidth !== width) setWidth(validatedWidth);
      if (validatedHeight !== height) setHeight(validatedHeight);
      
      // Create request body for our backend
      const requestBody = {
        userId,
        prompt,
        width: validatedWidth,
        height: validatedHeight,
        model: 'stable-diffusion-xl',
        enhancePrompt
      };
      
      addLog('info', 'Sending request to backend', { requestBody });
      
      // Use enhanced prompt from preview if available and user hasn't modified it
      if (previewEnhancedPrompt && enhancePrompt) {
        addLog('info', 'Using pre-fetched enhanced prompt');
        requestBody.prompt = previewEnhancedPrompt;
        // Don't re-enhance a prompt that's already been enhanced
        requestBody.enhancePrompt = false;
      }
      
      // Call our backend endpoint
      const response = await fetch('/api/marketing/text-to-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      addLog('info', 'Response received', { status: response.status });
      const data = await response.json();
      addLog('info', 'Response data', { data });
      
      if (!response.ok) {
        addLog('error', 'Error generating image', { error: data.error, status: response.status });
        throw new Error(data.error || 'Failed to generate image');
      }
      
      if (!data.imageUrl) {
        addLog('error', 'No image URL returned', { data });
        throw new Error('No image URL returned from the server');
      }
      
      setImageUrl(data.imageUrl);
      setEnhancedPrompt(data.enhancedPrompt);
      setOriginalPrompt(data.originalPrompt);
      addLog('success', 'Image generated successfully', { imageUrl: data.imageUrl });
      toast.success('Image generated successfully!');
      
      // Clear preview state after successful generation
      setPreviewEnhancedPrompt(null);
      setEditableEnhancedPrompt('');
      setIsEditingEnhancedPrompt(false);
    } catch (error) {
      addLog('error', 'Error generating image', { error });
      console.error('Error generating image:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate image. Please try again.');
    } finally {
      setLoading(false);
      addLog('info', 'Image generation request completed');
    }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        
        {/* Section Title with 3D effect */}
        <div className="relative mb-12 text-center">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 inline-block mb-2">
            Text to Image Generator
          </h2>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-pink-400 to-purple-500"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">Create professional images for your social media and marketing materials</p>
        </div>
      
      <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 shadow-xl rounded-xl p-8 mb-10 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50/40 to-purple-50/40 opacity-70"></div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 mb-6">Generate an Image</h2>
        
        {/* Quick examples section */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-3">Try an example:</h3>
          <div className="flex flex-wrap gap-2">
            {examplePrompts.map((example, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleUseExample(example)}
                className="text-xs px-3 py-1.5 bg-white/30 backdrop-blur-sm hover:bg-white/50 border border-white/30 rounded-full text-gray-700 transition-all duration-200 shadow-sm hover:shadow transform hover:-translate-y-0.5"
              >
                {example.length > 30 ? example.substring(0, 30) + '...' : example}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleGenerateImage}>
          <div className="mb-6">
            <label htmlFor="prompt" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2">
              Describe your image
            </label>
            <div className="relative">
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter a detailed description of the image you want to create..."
                className="w-full p-4 bg-white/50 backdrop-blur-sm border border-white/40 rounded-xl shadow-inner focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-200"
                rows={4}
                required
              />
              {prompt && (
                <button
                  type="button"
                  onClick={() => copyToClipboard(prompt)}
                  className="absolute top-3 right-3 px-2 py-1 bg-white/60 backdrop-blur-sm rounded-md text-pink-500 hover:text-pink-700 hover:bg-white/80 transition-all duration-200 text-xs font-medium shadow-sm hover:shadow"
                  title="Copy prompt"
                >
                  Copy
                </button>
              )}
            </div>
          </div>

          {/* Style presets */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2">
              Art Style (optional)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {stylePresets.map((style) => (
                <button
                  key={style.name}
                  type="button"
                  onClick={() => handleStyleChange(style.name)}
                  className={`py-2 px-3 rounded-xl text-xs backdrop-blur-sm transition-all duration-200 border shadow-sm hover:shadow transform hover:-translate-y-0.5 ${
                    selectedStyle === style.name
                      ? 'bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-pink-200 text-pink-700 font-medium'
                      : 'bg-white/30 border-white/40 text-gray-700 hover:bg-white/40'
                  }`}
                  title={style.description}
                >
                  {style.name}
                </button>
              ))}
            </div>
          </div>

          {/* Dimension presets */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2">
              Image Size
              <span className="ml-2 px-2 py-0.5 bg-white/40 backdrop-blur-sm rounded-full text-xs text-gray-700 font-medium">{width}x{height}</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {dimensionPresets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => handlePresetChange(preset.name)}
                  className={`py-2 px-3 rounded-xl text-xs backdrop-blur-sm transition-all duration-200 border shadow-sm hover:shadow transform hover:-translate-y-0.5 ${
                    selectedPreset === preset.name
                      ? 'bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-pink-200 text-pink-700 font-medium'
                      : 'bg-white/30 border-white/40 text-gray-700 hover:bg-white/40'
                  }`}
                  title={`${preset.width}x${preset.height} - ${preset.description}`}
                >
                  <span className="block">{preset.name.charAt(0).toUpperCase() + preset.name.slice(1)}</span>
                  <span className="block mt-1 opacity-75">{preset.width}x{preset.height}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="mb-6">
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl px-4 py-3 transition-all hover:bg-white/40 flex-grow mr-2">
                  <input
                    id="enhancePrompt"
                    type="checkbox"
                    checked={enhancePrompt}
                    onChange={(e) => setEnhancePrompt(e.target.checked)}
                    className="h-5 w-5 border-pink-300 rounded text-pink-500 focus:ring-pink-400 shadow-sm transition-all duration-200"
                  />
                  <label htmlFor="enhancePrompt" className="ml-3 text-sm font-medium text-gray-700">
                    Use AI to enhance my prompt for better results
                  </label>
                </div>
                
                {enhancePrompt && (
                  <button
                    type="button"
                    onClick={previewEnhancement}
                    disabled={!prompt || enhancementLoading}
                    className="px-4 py-3 bg-white/50 backdrop-blur-sm text-pink-600 rounded-xl text-sm hover:bg-white/70 border border-pink-200 transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center"
                  >
                    {enhancementLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-pink-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Enhancing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>Preview Enhanced</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              
              <div className="text-xs text-gray-500 italic mt-1 ml-1">
                AI enhancement adds technical details and clarity to your prompt for improved image quality
              </div>
              
              {enhancementError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{enhancementError}</span>
                  </div>
                </div>
              )}
              
              {previewEnhancedPrompt && (
                <div className="mt-3 p-4 bg-gradient-to-br from-pink-50/60 to-purple-50/60 border border-pink-100 rounded-xl">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-semibold text-pink-700">Enhanced Prompt Preview:</h4>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={toggleEnhancedPromptEdit}
                        className="px-2 py-1 bg-white/60 backdrop-blur-sm rounded-md text-pink-500 hover:text-pink-700 hover:bg-white/80 transition-all duration-200 text-xs font-medium shadow-sm hover:shadow flex items-center"
                      >
                        {isEditingEnhancedPrompt ? (
                          <>
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Save Changes</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            <span>Edit</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(previewEnhancedPrompt)}
                        className="px-2 py-1 bg-white/60 backdrop-blur-sm rounded-md text-pink-500 hover:text-pink-700 hover:bg-white/80 transition-all duration-200 text-xs font-medium shadow-sm hover:shadow"
                        title="Copy prompt"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  {isEditingEnhancedPrompt ? (
                    <textarea
                      value={editableEnhancedPrompt}
                      onChange={(e) => setEditableEnhancedPrompt(e.target.value)}
                      className="w-full p-3 bg-white/70 backdrop-blur-sm border border-pink-100 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent transition-all duration-200"
                      rows={4}
                    />
                  ) : (
                    <div className="p-3 bg-white/50 backdrop-blur-sm border border-pink-100 rounded-lg text-sm text-gray-800">
                      {previewEnhancedPrompt}
                    </div>
                  )}
                  
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={applyEnhancedPrompt}
                      className="px-3 py-1.5 bg-gradient-to-r from-pink-500/70 to-purple-500/70 text-white rounded-lg text-xs hover:from-pink-500 hover:to-purple-500 transition-all duration-200 shadow-sm hover:shadow flex items-center"
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      <span>Apply This Prompt</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none font-medium"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Creating Masterpiece...</span>
                </>
              ) : (
                <>
                  <span>Generate Image</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>

        {imageUrl ? (
          <div className="mt-10">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 mb-4">Generated Image</h3>
            <div className="relative border border-white/40 rounded-xl p-4 bg-white/20 backdrop-blur-sm shadow-xl overflow-hidden">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-pink-50/20 to-purple-50/20 opacity-50"></div>
              
              <div className="relative z-10">
                <img src={imageUrl} alt="Generated" className="max-w-full h-auto rounded-lg shadow-md" style={{ aspectRatio: `${width}/${height}` }} />
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const newWindow = window.open('', '_blank');
                      if (newWindow) newWindow.location.href = imageUrl;
                    }}
                    className="px-4 py-2 bg-white/60 backdrop-blur-sm text-gray-700 rounded-lg text-sm hover:bg-white/80 transition-all duration-200 shadow-sm hover:shadow transform hover:-translate-y-0.5 font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open Full Size
                  </button>
                  <a 
                    href={imageUrl} 
                    download="generated-image.png"
                    className="px-4 py-2 bg-gradient-to-r from-pink-500/80 to-purple-500/80 text-white rounded-lg text-sm hover:from-pink-500 hover:to-purple-500 transition-all duration-200 shadow-sm hover:shadow transform hover:-translate-y-0.5 font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </a>
                </div>
              </div>
            </div>
            
            {enhancedPrompt && enhancedPrompt !== originalPrompt && (
              <div className="mt-6">
                <h4 className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-2">AI-Enhanced Prompt Used:</h4>
                <div className="mt-2 p-4 bg-white/30 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm text-sm text-gray-800">
                  {enhancedPrompt}
                </div>
                
                <div className="mt-3">
                  <details className="text-sm group">
                    <summary className="cursor-pointer inline-flex items-center px-3 py-1 bg-white/40 backdrop-blur-sm rounded-full text-pink-600 hover:text-pink-700 hover:bg-white/60 transition-all duration-200 font-medium group-open:bg-pink-50/60 group-open:text-pink-700">
                      <svg className="w-4 h-4 mr-1.5 group-open:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      View Original Prompt
                    </summary>
                    <div className="mt-2 p-4 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-sm text-gray-800">
                      {originalPrompt}
                    </div>
                  </details>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-2">Preview</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center" 
                 style={{ aspectRatio: `${width}/${height}`, height: '300px' }}>
              <div className="text-center p-6">
                <p className="text-gray-500 mb-2">Your generated image will appear here</p>
                <p className="text-xs text-gray-400">Size: {width} × {height}px</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Developer Mode Toggle (hidden in the corner) */}
      <div className="fixed bottom-4 right-4 z-10">
        <button 
          onClick={toggleDeveloperMode}
          className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-all duration-200 shadow-md"
          title={developerMode ? "Disable Developer Mode" : "Enable Developer Mode"}
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>

      {/* Developer Logging Panel */}
      {developerMode && (
        <div className="bg-gray-900 text-gray-200 rounded-lg shadow-lg mb-6 overflow-hidden">
          <div className="p-3 bg-gray-800 flex justify-between items-center">
            <h3 className="text-sm font-mono font-medium flex items-center">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Developer Debug Console
            </h3>
            <div className="flex space-x-2">
              <button 
                onClick={clearLogs} 
                className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                title="Clear logs"
              >
                Clear
              </button>
              <button 
                onClick={copyLogs} 
                className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                title="Copy logs"
              >
                Copy
              </button>
            </div>
          </div>
          
          <div className="p-3 h-64 overflow-y-auto font-mono text-xs bg-gray-950" style={{ fontSize: '0.7rem' }}>
            {logs.length === 0 ? (
              <div className="text-gray-500 italic">
                No logs yet. Generate an image or preview an enhanced prompt to see activity.
              </div>
            ) : (
              <div>
                {logs.map((log, index) => {
                  let textColor = 'text-gray-300';
                  let badge = null;
                  
                  if (log.level === 'error') {
                    textColor = 'text-red-400';
                    badge = <span className="px-1 py-0.5 bg-red-500/20 text-red-300 rounded text-xs mr-1">ERROR</span>;
                  } else if (log.level === 'warning') {
                    textColor = 'text-yellow-400';
                    badge = <span className="px-1 py-0.5 bg-yellow-500/20 text-yellow-300 rounded text-xs mr-1">WARN</span>;
                  } else if (log.level === 'success') {
                    textColor = 'text-green-400';
                    badge = <span className="px-1 py-0.5 bg-green-500/20 text-green-300 rounded text-xs mr-1">SUCCESS</span>;
                  } else {
                    badge = <span className="px-1 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs mr-1">INFO</span>;
                  }
                  
                  return (
                    <div key={index} className={`mb-1.5 ${textColor}`}>
                      <span className="text-gray-500">[{log.timestamp}]</span> {badge} {log.message}
                      {log.data && (
                        <div className="pl-16 mt-0.5 text-gray-400 break-all whitespace-pre-wrap">
                          {JSON.stringify(log.data, null, 2)}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={logsEndRef} />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Tips for Getting the Best Results</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-lg">Be Specific and Detailed</h3>
            <p className="text-gray-600">The more details you provide in your prompt, the better the results. Include information about style, lighting, composition, and subject matter.</p>
            <p className="text-gray-600 mt-1"><strong>Example:</strong> "A professional photograph of a modern office space with natural lighting, minimalist furniture, plants, and a laptop on a desk" is better than "An office".</p>
          </div>
          
          <div>
            <h3 className="font-medium text-lg">Specify Art Style</h3>
            <p className="text-gray-600">Mention specific art styles or artists to influence the aesthetic of your image.</p>
            <p className="text-gray-600 mt-1"><strong>Example:</strong> "In the style of watercolor painting" or "Similar to works by [famous artist]".</p>
          </div>
          
          <div>
            <h3 className="font-medium text-lg">Use Descriptive Adjectives</h3>
            <p className="text-gray-600">Include adjectives that describe mood, atmosphere, and visual qualities.</p>
            <p className="text-gray-600 mt-1"><strong>Example:</strong> "Vibrant, sunny, cheerful" or "Moody, dramatic, low-key lighting".</p>
          </div>
          
          <div>
            <h3 className="font-medium text-lg">Specify Camera Settings</h3>
            <p className="text-gray-600">For photorealistic images, include details about camera settings.</p>
            <p className="text-gray-600 mt-1"><strong>Example:</strong> "Shot with a wide-angle lens" or "Shallow depth of field with background blur".</p>
          </div>
          
          <div>
            <h3 className="font-medium text-lg">Composition Guidance</h3>
            <p className="text-gray-600">Provide information about how elements should be arranged in the image.</p>
            <p className="text-gray-600 mt-1"><strong>Example:</strong> "Product in the foreground with blurred city skyline in the background" or "Overhead view of a breakfast table".</p>
          </div>
          
          <div>
            <h3 className="font-medium text-lg">AI Prompt Enhancement</h3>
            <p className="text-gray-600">Our AI prompt enhancement feature automatically improves your prompts to generate better quality images. It adds technical details and clarifies your intent to produce more accurate results.</p>
            <p className="text-gray-600 mt-1">You can toggle this feature on or off, and always view both the original and enhanced prompts after generation.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  </section>
  );
};

export default TextToImageGenerator;
